import React from 'react';
import { Delete, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Listing } from 'components/shared/TableListing';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { InputField, CheckboxField, SelectField } from 'components/form';

import { DealSheetFormValues } from './types';
import { ChargeAmountCell } from './ChargeAmountCell';

export const ChargesSection = () => {
  const { control } = useFormContext<DealSheetFormValues>();
  const { fields, append, remove, move } = useFieldArray({ control, name: 'charges' });

  return (
    <Listing
      tableData={fields}
      columns={[
        {
          renderKey: 'name',
          header: 'Charge/Fee Name',
          renderCell: ({ rowIndex }) => (
            <Stack gap="0.5rem">
              <InputField
                control={control}
                label="Charge/Fee Name"
                name={`charges.${rowIndex}.name`}
                inputProps={{ variant: 'outlined' }}
              />
            </Stack>
          ),
        },
        {
          renderKey: 'amountType',
          header: 'Type',
          renderCell: ({ rowIndex }) => (
            <SelectField
              label="Type"
              control={control}
              variant="outlined"
              name={`charges.${rowIndex}.amountType`}
              options={[
                { label: 'Fixed', value: 'flat' },
                { label: 'Percentage', value: 'percent' },
              ]}
            />
          ),
        },
        {
          renderKey: 'name',
          header: 'Amount',
          renderCell: ({ rowIndex }) => <ChargeAmountCell rowIndex={rowIndex} />,
        },
        {
          renderKey: 'description',
          header: 'Description',
          renderCell: ({ rowIndex }) => (
            <InputField
              control={control}
              label="Description"
              name={`charges.${rowIndex}.description`}
              inputProps={{ variant: 'outlined', multiline: true, minRows: 3 }}
            />
          ),
        },
        {
          renderKey: 'isTaxable',
          header: 'Taxable',
          width: '10rem',
          renderCell: ({ rowIndex }) => (
            <CheckboxField label="Taxable" control={control} name={`charges.${rowIndex}.isTaxable`} />
          ),
        },
        {
          renderKey: 'name',
          header: 'Action',
          width: '10rem',
          renderCell: ({ rowIndex }) => (
            <Stack direction="row" gap="0.5rem" alignItems="center">
              <Tooltip title="Move row down">
                <IconButton
                  size="small"
                  color="primary"
                  className="icon-btn-bordered"
                  disabled={rowIndex + 1 > fields.length - 1}
                  onClick={() => move(rowIndex, rowIndex + 1)}
                >
                  <KeyboardArrowDown />
                </IconButton>
              </Tooltip>

              <Tooltip title="Move row up">
                <IconButton
                  size="small"
                  color="primary"
                  className="icon-btn-bordered"
                  disabled={!rowIndex}
                  onClick={() => move(rowIndex, rowIndex - 1)}
                >
                  <KeyboardArrowUp />
                </IconButton>
              </Tooltip>

              <IconButton className="icon-btn-bordered" size="small" color="error" onClick={() => remove(rowIndex)}>
                <Delete />
              </IconButton>
            </Stack>
          ),
        },
      ]}
      actionPanelBottom={() => (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: '0.5rem' }}>
            Tip: use a negative amount to represent a discount (e.g. −$100 or −5%)
          </Typography>

          <Button
            size="small"
            onClick={() => {
              append({
                name: '',
                amount: 0,
                type: 'CHARGE',
                description: '',
                isTaxable: true,
                amountType: 'flat',
                isEquipmentLvl: false,
              });
            }}
          >
            Add Charge / Discount
          </Button>
        </Box>
      )}
    />
  );
};
